import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { BASEMAP_GROUPS, BASEMAP_LABEL, BASEMAP_SHORT, GOOGLE_KEY, MAPTILER_KEY, availableBasemaps, type Basemap } from "../lib/basemap";

/** Which base maps this user may use: keys we have ∩ providers enabled for their companies. null while loading. */
export function useBasemaps(isStaff: boolean | null | undefined) {
  const [list, setList] = useState<Basemap[] | null>(null);
  useEffect(() => {
    if (isStaff === undefined) return; // role not known yet
    if (isStaff) { setList(availableBasemaps("all")); return; }
    supabase.from("organisations").select("map_providers").then(({ data }) => {
      const all = new Set<string>();
      for (const o of data ?? []) for (const p of (o.map_providers as string[] | null) ?? []) all.add(p);
      setList(availableBasemaps([...all]));
    });
  }, [isStaff]);
  return list;
}

const LAYERS_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M12 3 2 8l10 5 10-5-10-5Z"/><path d="m2 13 10 5 10-5"/></svg>`;

/**
 * Map-type menu: a compact button showing the current layer; opens a panel grouped by provider
 * (OpenStreetMap: Streets / Hybrid / Satellite · Google: Map / Hybrid / Satellite).
 * `setupHints` (staff only) explains why a provider is missing.
 */
export default function BasemapPicker({ value, options, onChange, setupHints = false }: {
  value: Basemap; options: Basemap[] | null; onChange: (b: Basemap) => void; setupHints?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  if (!options || (options.length < 2 && !setupHints)) return null;

  const groups = BASEMAP_GROUPS.map(([g, list]) => [g, list.filter((b) => options.includes(b))] as const);
  const hints: string[] = [];
  if (setupHints && !MAPTILER_KEY) hints.push("Satellite/Hybrid: VITE_MAPTILER_KEY isn't set in this build.");
  if (setupHints && !GOOGLE_KEY) hints.push("Google: VITE_GOOGLE_MAPS_KEY isn't set in this build (add it in Netlify, then redeploy).");

  return (
    <div className="bm-pick" ref={ref}>
      <button className="bm-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="true" title="Map type">
        <span dangerouslySetInnerHTML={{ __html: LAYERS_ICON }} />
        <span>{BASEMAP_LABEL[value]}</span>
        <span className="bm-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="bm-menu" role="menu">
          {groups.filter(([, list]) => list.length).map(([g, list]) => (
            <div key={g} className="bm-group">
              <div className="bm-group-h">{g}</div>
              <div className="bm-opts">
                {list.map((b) => (
                  <button key={b} role="menuitemradio" aria-checked={value === b} className={`bm-opt bm-${b} ${value === b ? "on" : ""}`}
                    onClick={() => { onChange(b); setOpen(false); }}>
                    <span className="bm-thumb" aria-hidden="true" />
                    <span>{BASEMAP_SHORT[b]}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {hints.map((h) => <p key={h} className="bm-hint">{h}</p>)}
        </div>
      )}
    </div>
  );
}
